import React, { useEffect, useState } from "react";
import { AWARDS_INFO } from "@/data/constants";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/components/seo/SEO";
import { Ticket, MapPin, Calendar, Trophy, Sparkles, Users, Mic, Camera } from "lucide-react";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff / 3600000) % 24);
  const m = Math.floor((diff / 60000) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s };
}

export default function Awards() {
  const { d, h, m, s } = useCountdown(AWARDS_INFO.date_iso);
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", interest: "Platinum Sponsor", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post("/sponsor-inquiries", form); setSent(true); toast.success("Inquiry sent — thank you."); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const cats = AWARDS_INFO.categories;

  return (
    <div data-testid="awards-page" className="pt-16 md:pt-20">
      <SEO
        title="First Annual YCN Awards 2027"
        description={`The First Annual YCN Awards — May 1, 2027 at the Marriott Virginia Beach Oceanfront Resort. A red-carpet celebration of excellence in entertainment, storytelling, entrepreneurship, and community impact. Tickets from $${AWARDS_INFO.ticket_price_usd}.`}
        path="/awards"
      />
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#050505]" />
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(60% 60% at 20% 30%, rgba(124,58,237,0.35), transparent 60%), radial-gradient(50% 50% at 85% 70%, rgba(249,115,22,0.35), transparent 60%), radial-gradient(40% 40% at 50% 50%, rgba(59,130,246,0.25), transparent 60%)",
        }} />
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.06) 50%, transparent 60%)" }} />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full ycn-glass text-[11px] uppercase tracking-[0.35em]">
              <Trophy size={13} className="text-orange-300" /> First Annual
            </div>
            <h1 className="font-display text-6xl md:text-8xl font-black leading-[0.9] tracking-tight uppercase">
              <span className="ycn-gradient-text">YCN</span><br />
              Awards
            </h1>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
              <div className="ycn-glass rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1"><Calendar size={12} className="inline mr-1.5" /> Date</div>
                <div className="font-display text-xl font-bold">{AWARDS_INFO.date_display}</div>
              </div>
              <div className="ycn-glass rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1"><MapPin size={12} className="inline mr-1.5" /> Venue</div>
                <div className="text-sm font-semibold leading-tight">{AWARDS_INFO.venue}</div>
                <div className="text-[11px] text-white/60 mt-0.5">{AWARDS_INFO.city}</div>
              </div>
              <div className="ycn-glass rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1"><Ticket size={12} className="inline mr-1.5" /> Tickets</div>
                <div className="font-display text-xl font-bold">${AWARDS_INFO.ticket_price_usd}</div>
                <div className="text-[11px] text-white/60">General Admission</div>
              </div>
            </div>

            {/* Countdown */}
            <div className="mt-8 flex flex-wrap gap-3">
              {[{ n: d, l: "Days" }, { n: h, l: "Hours" }, { n: m, l: "Minutes" }, { n: s, l: "Seconds" }].map((u) => (
                <div key={u.l} data-testid={`countdown-${u.l.toLowerCase()}`} className="ycn-glass rounded-2xl p-4 min-w-[92px] text-center">
                  <div className="font-display text-3xl md:text-4xl font-black tabular-nums ycn-gradient-text">{u.n.toString().padStart(2, "0")}</div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mt-1">{u.l}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#tickets" data-testid="awards-tickets-cta" className="ycn-btn-primary inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-white transition-all">
                <Ticket size={16} /> Get Tickets
              </a>
              <a href="#sponsor" data-testid="awards-sponsor-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full ycn-glass hover:bg-white/15 font-semibold transition">
                Become a Sponsor
              </a>
              <a href="/submit" data-testid="awards-submit-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/20 hover:bg-white/10 font-semibold transition">
                Submit for Consideration
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="px-6 md:px-12 py-16">
        <div className="max-w-[1200px] mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-3">About the Awards</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">Celebrating <span className="ycn-gradient-text">Excellence</span> in Storytelling</h2>
            <p className="mt-5 text-white/75 leading-relaxed">
              The First Annual YCN Awards celebrate excellence in entertainment, independent media, storytelling, entrepreneurship, culture, and community impact. Join creators, artists, industry leaders, and community changemakers for a red-carpet celebration of the voices shaping the future of entertainment.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Sparkles, l: "Red Carpet" },
              { icon: Trophy, l: "Awards Ceremony" },
              { icon: Mic, l: "Live Entertainment" },
              { icon: Users, l: "Networking" },
              { icon: Camera, l: "Creator Recognition" },
              { icon: Trophy, l: "Community Impact" },
            ].map(({ icon: Icon, l }) => (
              <div key={l} className="ycn-glass rounded-2xl p-5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-600/40 via-blue-600/40 to-orange-500/40 border border-white/10 flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <div className="text-sm font-semibold">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 md:px-12 py-16 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-2">Award Categories</div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold">The <span className="ycn-gradient-text">Categories</span></h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="award-categories-grid">
            {cats.map((c, i) => (
              <div key={c} className="ycn-glass rounded-2xl p-6 relative overflow-hidden group hover:border-white/20 transition">
                <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full blur-2xl opacity-20 bg-gradient-to-br from-violet-500 via-blue-500 to-orange-500 group-hover:opacity-40 transition-opacity" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Category · {String(i + 1).padStart(2, "0")}</div>
                <div className="font-display text-xl font-bold leading-tight">{c}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center text-sm text-white/50">Nominees will be announced. Content and categories are editable via the YCN Admin CMS.</div>
        </div>
      </section>

      {/* Tickets */}
      <section id="tickets" className="px-6 md:px-12 py-16 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-2">Tickets</div>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold">Reserve <span className="ycn-gradient-text">Your Seat</span></h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">General admission tickets are available at ${AWARDS_INFO.ticket_price_usd}. VIP, table packages, and sponsor tickets will be announced.</p>
          <div className="mt-8 max-w-md mx-auto ycn-glass rounded-3xl p-8 border border-white/10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">General Admission</div>
            <div className="mt-2 font-display text-6xl font-black ycn-gradient-text">${AWARDS_INFO.ticket_price_usd}</div>
            <div className="mt-1 text-sm text-white/60">per ticket</div>
            <button data-testid="tickets-notify-btn" onClick={() => toast("Ticket sales open soon. We'll notify you.")} className="ycn-btn-primary mt-6 w-full py-3 rounded-full font-bold text-white transition-all">
              Notify Me When Tickets Are Live
            </button>
          </div>
        </div>
      </section>

      {/* Sponsor form */}
      <section id="sponsor" className="px-6 md:px-12 py-16 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-2">Sponsorship</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">Partner With <span className="ycn-gradient-text">the YCN Awards</span></h2>
            <p className="mt-4 text-white/70">Align your brand with a premier entertainment moment. Presenting, Platinum, Gold and Supporting opportunities are available.</p>
            <ul className="mt-6 space-y-2 text-sm text-white/70">
              <li>• Presenting Sponsor</li>
              <li>• Platinum Sponsors</li>
              <li>• Gold Sponsors</li>
              <li>• Supporting Partners</li>
            </ul>
          </div>
          {sent ? (
            <div className="ycn-glass rounded-3xl p-10 text-center" data-testid="sponsor-success">
              <Trophy size={40} className="mx-auto text-orange-300" />
              <h3 className="mt-4 font-display text-2xl font-bold ycn-gradient-text">Inquiry received.</h3>
              <p className="text-white/70 mt-2">A member of the YCN team will reach out shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="ycn-glass rounded-3xl p-8 space-y-3" data-testid="sponsor-form">
              {[
                { k: "company", p: "Company", req: true },
                { k: "contact_name", p: "Contact name", req: true },
                { k: "email", p: "Email", req: true, type: "email" },
                { k: "phone", p: "Phone" },
              ].map((f) => (
                <input key={f.k} data-testid={`sponsor-${f.k}`} type={f.type || "text"} required={f.req} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.p} className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40 transition" />
              ))}
              <select data-testid="sponsor-interest" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}
                className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 outline-none px-5 py-3 text-white transition">
                {["Presenting Sponsor", "Platinum Sponsor", "Gold Sponsor", "Supporting Partner"].map((o) => <option key={o} value={o} className="bg-[#0f0f11]">{o}</option>)}
              </select>
              <textarea data-testid="sponsor-message" rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How would you like to partner?" className="w-full rounded-2xl bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40 transition" />
              <button data-testid="sponsor-submit" disabled={submitting} className="ycn-btn-primary w-full py-3 rounded-full font-bold text-white disabled:opacity-60 transition-all">
                {submitting ? "Sending…" : "Submit Inquiry"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
